import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

/**
 * Hook to detect and track PWA installations
 * When a user opens the app in standalone mode (PWA), we update their tracking record
 */
export const usePWAInstallTracking = () => {
  useEffect(() => {
    const isPWA = 
      window.matchMedia('(display-mode: standalone)').matches ||
      // @ts-ignore - Safari on iOS
      window.navigator.standalone === true ||
      document.referrer.includes('android-app://');

    if (!isPWA) return;

    const trackInstallation = async () => {
      try {
        // Get the current user's profile to find their phone number
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: profile } = await supabase
          .from("profiles")
          .select("phone, full_name")
          .eq("id", user.id)
          .single();

        if (!profile?.phone) return;

        // Detect device type
        const userAgent = navigator.userAgent;
        let deviceType = "Unknown";
        if (/iPhone|iPad|iPod/.test(userAgent)) {
          deviceType = "iOS";
        } else if (/Android/.test(userAgent)) {
          deviceType = "Android";
        } else if (/Windows/.test(userAgent)) {
          deviceType = "Windows";
        } else if (/Mac/.test(userAgent)) {
          deviceType = "macOS";
        }

        // Check if we've already tracked this installation
        const storageKey = `pwa_install_tracked_${profile.phone}`;
        if (localStorage.getItem(storageKey)) return;

        // Try to update existing tracking record (if message was sent)
        const { data: existingRecord } = await supabase
          .from("pwa_install_tracking")
          .select("id, status")
          .eq("customer_phone", profile.phone)
          .eq("status", "message_sent")
          .order("created_at", { ascending: false })
          .limit(1)
          .single();

        if (existingRecord) {
          // Update the existing record
          await supabase
            .from("pwa_install_tracking")
            .update({
              installed_at: new Date().toISOString(),
              user_agent: userAgent,
              device_type: deviceType,
              status: "installed",
            })
            .eq("id", existingRecord.id);
        } else {
          // Create a new record for organic installs (no message sent)
          await supabase
            .from("pwa_install_tracking")
            .insert({
              customer_phone: profile.phone,
              customer_name: profile.full_name,
              installed_at: new Date().toISOString(),
              user_agent: userAgent,
              device_type: deviceType,
              status: "installed",
            });
        }

        // Mark as tracked to avoid duplicate entries
        localStorage.setItem(storageKey, "true");
        console.log("PWA installation tracked successfully");
      } catch (error) {
        console.error("Error tracking PWA installation:", error);
      }
    };

    trackInstallation();
  }, []);
};

export default usePWAInstallTracking;
